import {
    Action,
    getModule,
    Module,
    Mutation,
    VuexModule
} from 'vuex-module-decorators';
import { transformUnit } from '~/filters/unitConverter';
import { uppercaseFirstCharacter } from '~/helpers';
import { FieldObject } from '~/interfaces/field.interface';
import { OrderType } from '~/interfaces/order.interface';
import {
    AccountValidation,
    AdministrativeUser,
    ApplicationEliot,
    BrandsConsents,
    BrandsConsentsHistory,
    Consent,
    CorporateEliot,
    DevApplication,
    Legal,
    LegalEntry,
    MailType,
    MarketingBrand,
    MarketingConsentsKeys,
    MarketingsConsents,
    UserApplication,
    UserData,
    UserState
} from '~/interfaces/user.interface';
import apiService from '~/services/api.service';
import { ConfigModule } from '~/store/modules/config';
import store from '~common-store';
import { LoadingModule } from '~common-store/modules/loading';
import { NotificationsModule } from '~common-store/modules/notifications';
import { PopupModule } from '~common-store/modules/popup';

@Module({ dynamic: true, store, name: 'user', namespaced: true })
class User extends VuexModule implements UserState {
    id = '';
    userMail = '';
    linked: string[] | undefined; // ['legrand']
    linkedUsers: { [key: string]: string } | undefined = {} as {
        [key: string]: string;
    }; // legrand: 'id linked user'
    administrative: AdministrativeUser = {} as AdministrativeUser;
    accountValidation: AccountValidation = {} as AccountValidation;
    legal: Legal = {} as Legal;
    applications: UserApplication[] = [] as UserApplication[];
    applicationsEliot: ApplicationEliot[] = [] as ApplicationEliot[];
    corporatesEliot: CorporateEliot[] = [] as CorporateEliot[];
    devApplications: DevApplication[] = [];
    prefix = '';
    consents: LegalEntry[] = [];
    brandsConsents: BrandsConsents = {} as BrandsConsents;
    brandsConsentsHistory: BrandsConsentsHistory = {} as BrandsConsentsHistory;
    marketingsConsents: MarketingsConsents = {} as MarketingsConsents;
    mailTypesSkipped: MailType[] = [] as MailType[];
    env?: string = '';
    orders: OrderType[] = [];

    get preferences() {
        const preferences: FieldObject[] = [
            { key: 'country', value: this.administrative.country },
            { key: 'language', value: this.administrative.lang },
        ];
        if (!UserModule.isEliot) {
            preferences.push({
                key: 'analytics_consent',
                value: this?.legal?.app_telemetry ? 'Accepted' : 'Not accepted',
            });
        }
        const { profiling, marketingCampaigns } = this.legal || {};
        if (typeof marketingCampaigns !== 'undefined') {
            preferences.push({
                key: 'marketingCampaigns',
                value: marketingCampaigns,
                type: marketingCampaigns ? 'date' : 'boolean',
                class: marketingCampaigns ? 'success--text' : 'error--text',
            });
        }
        if (typeof profiling !== 'undefined') {
            preferences.push({
                key: 'profiling',
                value: profiling,
                type: profiling ? 'date' : 'boolean',
                class: profiling ? 'success--text' : 'error--text',
            });
        }
        return preferences;
    }

    get units(): FieldObject[] {
        return [
            {
                key: 'unit',
                value: transformUnit(this.administrative.unit, 'unit'),
            }, // metric 0, us 1
            {
                key: 'windunit',
                value: transformUnit(this.administrative.windunit, 'windunit'),
            },
            {
                key: 'pressureunit',
                value: transformUnit(
                    this.administrative.pressureunit,
                    'pressureunit',
                ),
            },
            {
                key: 'feel_like_algo',
                value: transformUnit(
                    this.administrative.feel_like_algo,
                    'feels_like_algo',
                ),
            },
        ];
    }

    get userPersonalInformation(): FieldObject[] {
        return this.isEliot
            ? [
                { key: 'givenName', value: this.administrative.givenName },
                { key: 'surname', value: this.administrative.surname },
                {
                    key: 'phoneNumber',
                    value: this.administrative.phoneNumber,
                },
            ]
            : [];
    }

    get emailSettings(): FieldObject[] {
        if (this.accountValidation) {
            return [
                {
                    key: 'validated_mail',
                    value: this.accountValidation.validated_mail,
                    type: 'boolean',
                    class: this.accountValidation.validated_mail
                        ? 'success--text'
                        : 'error--text',
                },
            ];
        } else {
            return [];
        }
    }

    get gdprConsents(): { [key: string]: FieldObject[] } {
        const gdprConsents: { [key: string]: FieldObject[] } = {};
        this.consents?.forEach((consent) => {
            // do not push dev_terms_of_use if app is not app_netatmo
            if (
                consent.document_type !== 'dev_terms_of_use' ||
                consent.app_type === 'app_netatmo'
            ) {
                gdprConsents[consent.app_type] =
                    gdprConsents[consent.app_type] ?? [];
                gdprConsents[consent.app_type].push({
                    key: consent.document_type,
                    value: consent.last_consent ?? 'pending',
                    type: consent.last_consent ? 'date' : 'string',
                    class: consent.last_consent
                        ? 'success--text'
                        : 'error--text',
                });
            }
        });
        return gdprConsents;
    }

    get prefixedUserMail(): string {
        return this.prefix + this.userMail;
    }

    get userType(): string {
        if (this.isEliot) {
            return 'eliot';
        }
        switch (this.prefix) {
            case '':
                return 'netatmo';
            case 'sdbg://':
                return 'vaillant';
            default:
                return this.prefix.replace('://', '');
        }
    }

    get getUserTopologyScope(): string {
        return `Topology${uppercaseFirstCharacter(this.userType)}`;
    }

    get otherAdmins(): string[] | null | undefined {
        if (this.linkedUsers && Object.keys(this.linkedUsers).length > 0) {
            switch (process.env.VUE_APP_PREFIX) {
                case 'netatmo':
                    if (
                        Object.keys(this.linkedUsers).indexOf('legrand') !== -1
                    ) {
                        return ['legrand'];
                    }
                    return null;
                case 'legrand':
                    if (
                        Object.keys(this.linkedUsers).indexOf('netatmo') !== -1
                    ) {
                        return ['netatmo'];
                    }
                    return null;
                default:
                    return null;
            }
        }
    }

    get shouldGetMarketingConsents(): boolean {
        // only make the call when on netatmo or legrand admin
        return ['netatmo', 'legrand'].includes(ConfigModule.environmentPrefix);
    }

    get isEliot(): boolean {
        return this.env === 'eliot';
    }

    get interestsMarketingConsents() {
        return getMarketingsConsentsByType('thematic', this.marketingsConsents);
    }

    get channelsMarketingConsents() {
        return getMarketingsConsentsByType('channel', this.marketingsConsents);
    }

    @Action
    async setUser(user: UserData): Promise<void> {
        this.context.commit('SET_USER', user);
    }

    @Action
    reset(): void {
        this.context.commit('RESET_USER');
        this.context.commit('SET_USER_BRANDS_CONSENTS', {
            brandsConsents: {},
            brandsConsentsHistory: {},
        });
    }

    @Action
    async deleteAccount(): Promise<void> {
        try {
            if (this.isEliot) {
                await apiService.deleteeliotuser(
                    { user_mail: this.userMail },
                    'User account successfully deleted',
                );
            } else {
                await apiService.admindeleteaccount(
                    { user_id: this.id },
                    'User account successfully deleted',
                );
            }
            window.location.href = ConfigModule.supportUrl;
        } catch (error) {
            console.error(error);
        }
    }

    @Action
    async deleteIdentityKeys(): Promise<void> {
        try {
            await apiService.deleteidentitykeys(
                { user_id: this.id },
                'User identity keys successfully deleted',
            );
        } catch (error) {
            console.error(error);
        }
    }

    @Action
    async deleteEmptyHomes(): Promise<void> {
        try {
            await apiService.deleteemptyhomes(
                { user_id: this.id },
                'User empty homes successfully deleted',
            );
        } catch (error) {
            console.error(error);
        }
    }

    @Action
    setPrefix(prefix: string): void {
        this.context.commit('SET_PREFIX', prefix);
    }

    // get data of marketings consents from api
    @Action
    async getUserMarketingsConsents(): Promise<void> {
        try {
            const response = await apiService.getUserMarketingsConsents({
                user_id: this.id,
            });
            const data = response?.data;
            if (data) {
                const filterArray = Object.entries(data).filter(
                    (element) =>
                        element[0].startsWith('thematic') ||
                        element[0].startsWith('channel'),
                );
                const ObjectFromFilterArray = Object.fromEntries(filterArray);
                this.context.commit(
                    'INSERT_MARKETINGS_CONSENTS',
                    ObjectFromFilterArray,
                );
            }
        } catch (error) {
            this.context.commit('INSERT_MARKETINGS_CONSENTS', null);
            switch (error?.response?.data?.error?.code) {
                // show no error message if id is invalid (code 21)
                case 21:
                    return;
                default:
                    NotificationsModule.pushNotification({
                        title: 'Error',
                        text:
                            'There was an error while fetching the Marketings consents of the user',
                        type: 'error',
                        duration: 10000,
                    });
            }
        }
    }

    // post new consents -> after click on apply
    @Action
    async unsubscribeUserMarketingsConsents(): Promise<void> {
        try {
            await apiService.unsubscribeUserMarketingsConsents({
                user_id: this.id,
                consents: this.marketingsConsents,
            });
            NotificationsModule.pushNotification({
                title: 'Success',
                text: 'User unsubscribing succeeded',
                type: 'success',
            });
            this.setUserConsent({ brand: 'netatmo', consent: false });
            this.context.commit('INSERT_MARKETINGS_CONSENTS', null);
        } catch (error) {
            NotificationsModule.pushNotification({
                title: 'Error',
                text: 'User unsubscribing failed',
                type: 'error',
            });
        }
    }

    @Action
    async getUserConsents(): Promise<void> {
        try {
            let brandsConsents = {} as Partial<BrandsConsents>;
            let brandsConsentsHistory = {} as Partial<BrandsConsentsHistory>;
            let filteredBrandsConsentsHistory;
            if (this.shouldGetMarketingConsents) {
                brandsConsentsHistory = (
                    await apiService.getUserConsent({ user_id: this.id })
                ).data.body;
                if (brandsConsentsHistory) {
                    let filteredBrands: string[] = [];
                    switch (ConfigModule.environmentPrefix) {
                        case 'netatmo':
                        case 'legrand':
                            switch (this.prefix) {
                                case '':
                                    filteredBrands = [
                                        'netatmo',
                                        'bticino',
                                        'legrand',
                                    ];
                                    break;
                                default:
                                    filteredBrands = [
                                        this.prefix.split('://')[0],
                                    ];
                            }
                            break;
                        default:
                            filteredBrands = [ConfigModule.environmentPrefix];
                    }
                    filteredBrandsConsentsHistory = Object.fromEntries(
                        Object.entries(brandsConsentsHistory).filter(
                            ([brand]) => {
                                return filteredBrands.includes(brand);
                            },
                        ),
                    );
                    // build the brandsConsents object { netatmo: boolean, legrand: boolean, bticino: boolean }
                    // from the last consent value for each brand from the history
                    const consentsEntries = Object.entries(
                        filteredBrandsConsentsHistory,
                    ).map(([brand, consentHistory]: [string, Consent[]]) => {
                        return [
                            brand,
                            consentHistory.slice(-1)[0]?.consent || false,
                        ];
                    }) as Array<[string, boolean]>;
                    brandsConsents = {} as BrandsConsents;
                    consentsEntries.forEach(([brand, consent]) => {
                        brandsConsents[
                            brand as 'netatmo' | 'legrand' | 'bticino'
                        ] = consent;
                    });
                }
            }
            this.context.commit('SET_USER_BRANDS_CONSENTS', {
                brandsConsents,
                brandsConsentsHistory: filteredBrandsConsentsHistory,
            });
        } catch (error) {
            switch (error?.response?.data?.error?.code) {
                // show no error message if id is invalid (code 21)
                case 21:
                    return;
                default:
                    NotificationsModule.pushNotification({
                        title: 'Error',
                        text:
                                'There was an error while fetching the brandsConsents history of the user',
                        type: 'error',
                        duration: 10000,
                    });
            }
        }
    }

    @Action
    setUserConsent({
        brand,
        consent,
    }: {
        brand: MarketingBrand;
        consent: boolean;
    }): void {
        this.context.commit('SET_USER_BRAND_CONSENT', { brand, consent });
    }

    @Action
    async removeUserFromHome({
        homeId,
        userId,
    }: {
        homeId: string;
        userId: string;
    }): Promise<void> {
        const params = {
            home_id: homeId,
            user_id: userId,
        };
        await apiService.removeUserFromHome(
            params,
            'User successfully deleted from home',
        );
        if (userId === this.id) {
            // if removed user is current user, reload to its info page
            window.location.href = `${
                ConfigModule.supportUrl
            }/user/${encodeURIComponent(this.prefixedUserMail)}`;
        } else {
            // else reload to the home info page
            window.location.href = `${ConfigModule.supportUrl}/home/${homeId}`;
        }
    }

    @Action
    handleChangeContentsUser(key: string) {
        this.context.commit('UPDATE_CONSENTS', key);
    }

    @Action({ commit: 'SET_USER_ORDERS' })
    async getUserOrders() {
        try {
            LoadingModule.showLoading(true);
            const response = await apiService.admingetuserorders({ user_id: UserModule.id });
            LoadingModule.showLoading(false);
            return response.data.results as OrderType[];
        } catch (error) {
            return [];
        } finally {
            PopupModule.setLastExecuted(new Date());
        }
    }

    @Mutation
    UPDATE_CONSENTS(key: MarketingConsentsKeys): void {
        this.marketingsConsents[key] = !this.marketingsConsents[key];
    }

    // Update all new consents marketing after fetch it
    @Mutation
    INSERT_MARKETINGS_CONSENTS(data: MarketingsConsents): void {
        if (!data) {
            data = {
                thematicPromotional: false,
                thematicComfort: false,
                thematicWeather: false,
                thematicSecurity: false,
                thematicSustainability: false,
                channelEmail: false,
                channelPush: false,
                channelInApp: false,
            };
        }
        this.marketingsConsents = data;
    }

    @Mutation
    RESET_USER(): void {
        this.id = '';
        this.administrative = {} as AdministrativeUser;
        this.accountValidation = {} as AccountValidation;
        this.legal = {} as Legal;
        this.consents = [];
        this.userMail = '';
        this.linked = undefined;
        this.linkedUsers = undefined;
        this.applications = [];
        this.applicationsEliot = [];
        this.devApplications = [];
        this.corporatesEliot = [];
        this.mailTypesSkipped = [];
        this.env = undefined;
    }

    @Mutation
    SET_USER(user: UserData): void {
        this.id = user.user_id;
        // remove sdbg or vaillant prefix from backend response (we already store it in UserModule.prefix)
        this.userMail = user.user_mail?.replace(
            /sdbg:\/\/|vaillant:\/\/|muller:\/\/|velux:\/\//,
            '',
        );
        this.administrative = user.administrative;
        this.accountValidation = user.account_validation;
        this.legal = user.legal;
        this.consents = user.consents;
        this.linked = user.linked;
        this.linkedUsers = user.linkedUsers;
        this.applications = user.applications;
        this.applicationsEliot = user.applications_eliot;
        this.devApplications = user.dev_applications;
        this.corporatesEliot = user.corporates_eliot;
        this.mailTypesSkipped = user.mail_types_skipped;
        this.env = user.env;
    }

    @Mutation
    SET_PREFIX(prefix: string): void {
        this.prefix = prefix;
    }

    @Mutation
    SET_USER_BRANDS_CONSENTS({
        brandsConsents,
        brandsConsentsHistory,
    }: {
        brandsConsents: BrandsConsents;
        brandsConsentsHistory: BrandsConsentsHistory;
    }): void {
        const { netatmo } = brandsConsents;
        this.brandsConsents = { netatmo };
        this.brandsConsentsHistory = brandsConsentsHistory;
    }

    @Mutation
    SET_USER_BRAND_CONSENT({
        brand,
        consent,
    }: {
        brand: MarketingBrand;
        consent: boolean;
    }): void {
        this.brandsConsents[brand] = consent;
    }

    @Mutation
    SET_USER_ORDERS(orders: OrderType[]) {
        this.orders = orders;
    }
}

const getMarketingsConsentsByType = (
    filterString: string,
    dataConsents: MarketingsConsents,
) => {
    const filterArray = Object.entries(dataConsents).filter((element) =>
        element[0].startsWith(filterString),
    );
    return Object.fromEntries(filterArray);
};

export const UserModule = getModule(User);
